import * as React from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { SelectField } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toaster } from "@/components/Toaster";
import { handleMutationError } from "@/lib/utils";
import { OrgLogo } from "./OrgLogo";

import {
	getEvents,
	getSubEvents,
	getWorkshops,
	getFaculties,
	submitRegistration,
	getRegistrationStatus,
	type FeeCategoryOption,
} from "./events.api";
import { eventRegistrationSchema, type EventRegistrationFormData } from "./event-registration.schema";

const formatGHS = (amount: number) =>
	new Intl.NumberFormat("en-GB", { style: "currency", currency: "GHS", minimumFractionDigits: 2 }).format(amount || 0);

const TITLE_OPTIONS = [
	{ id: "Prof", name: "Prof" },
	{ id: "Dr", name: "Dr" },
	{ id: "Mr", name: "Mr" },
	{ id: "Mrs", name: "Mrs" },
	{ id: "Miss", name: "Miss" },
];

const MOMO_NETWORK_OPTIONS = [
	{ id: "MTN", name: "MTN" },
	{ id: "VODAFONE", name: "Vodafone" },
	{ id: "AIRTELTIGO", name: "AirtelTigo" },
];

const emptyForm: EventRegistrationFormData = {
	event_id: "",
	sub_event_id: "",
	workshop_id: "",
	fee_category_id: "",
	title: "",
	first_name: "",
	last_name: "",
	phone: "",
	email: "",
	confirm_email: "",
	faculty_id: "",
	mdc_number: "",
	category_other: "",
	senior_waiver_applied: false,
	payment_method: "card",
	momo_mobile_number: "",
	momo_mobile_network: "",
};

type ViewState = "form" | "waiting-momo" | "result";

export function EventRegistrationPage() {
	const [view, setView] = React.useState<ViewState>("form");
	const [pollTransactionUuid, setPollTransactionUuid] = React.useState<string | null>(null);
	const [resultStatus, setResultStatus] = React.useState<{ success: boolean; message: string } | null>(null);

	const form = useForm<EventRegistrationFormData>({
		resolver: zodResolver(eventRegistrationSchema),
		defaultValues: emptyForm,
	});

	const eventId = form.watch("event_id");
	const subEventId = form.watch("sub_event_id");
	const workshopId = form.watch("workshop_id");
	const feeCategoryId = form.watch("fee_category_id");
	const paymentMethod = form.watch("payment_method");

	const eventsQuery = useQuery({ queryKey: ["public-events"], queryFn: getEvents });
	const events = eventsQuery.data?.data ?? [];

	const subEventsQuery = useQuery({
		queryKey: ["public-sub-events", eventId],
		queryFn: () => getSubEvents(Number(eventId)),
		enabled: !!eventId,
	});
	const subEvents = subEventsQuery.data?.data ?? [];
	const selectedSubEvent = subEvents.find((s: { id: number }) => String(s.id) === subEventId);

	const workshopsQuery = useQuery({
		queryKey: ["public-workshops", subEventId],
		queryFn: () => getWorkshops(Number(subEventId)),
		enabled: !!subEventId && !!selectedSubEvent?.has_workshops,
	});
	const workshops = workshopsQuery.data?.data ?? [];
	const selectedWorkshop = workshops.find((w: { id: number }) => String(w.id) === workshopId);

	const facultiesQuery = useQuery({ queryKey: ["public-faculties"], queryFn: getFaculties });
	const faculties = facultiesQuery.data?.data ?? [];

	const feeCategories: FeeCategoryOption[] = selectedSubEvent?.has_workshops
		? selectedWorkshop?.fee_categories ?? []
		: selectedSubEvent?.fee_categories ?? [];
	const selectedFeeCategory = feeCategories.find((c) => String(c.id) === feeCategoryId);

	const seniorWaiverApplied = form.watch("senior_waiver_applied");
	const resolvedFee = selectedFeeCategory
		? seniorWaiverApplied && selectedFeeCategory.waiver_field_label
			? 0
			: selectedFeeCategory.current_fee
		: null;

	// Reset downstream selections when an upstream selection changes.
	React.useEffect(() => {
		form.setValue("sub_event_id", "");
		form.setValue("workshop_id", "");
		form.setValue("fee_category_id", "");
	}, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

	React.useEffect(() => {
		form.setValue("workshop_id", "");
		form.setValue("fee_category_id", "");
	}, [subEventId]); // eslint-disable-line react-hooks/exhaustive-deps

	React.useEffect(() => {
		form.setValue("fee_category_id", "");
	}, [workshopId]); // eslint-disable-line react-hooks/exhaustive-deps

	const submitMutation = useMutation({
		mutationFn: (data: EventRegistrationFormData) =>
			submitRegistration({
				event_id: Number(data.event_id),
				sub_event_id: Number(data.sub_event_id),
				workshop_id: data.workshop_id ? Number(data.workshop_id) : undefined,
				fee_category_id: Number(data.fee_category_id),
				title: data.title || undefined,
				first_name: data.first_name,
				last_name: data.last_name,
				phone: data.phone,
				email: data.email,
				faculty_id: data.faculty_id ? Number(data.faculty_id) : undefined,
				mdc_number: data.mdc_number || undefined,
				category_other: data.category_other || undefined,
				senior_waiver_applied: data.senior_waiver_applied,
				payment_method: data.payment_method,
				momo_mobile_number: data.payment_method === "momo" ? data.momo_mobile_number || undefined : undefined,
				momo_mobile_network: data.payment_method === "momo" ? (data.momo_mobile_network || undefined) : undefined,
			}),
		onSuccess: (response) => {
			const result = response.data;

			if (!result.requires_payment) {
				setResultStatus({ success: true, message: "Your registration is complete." });
				setView("result");
				return;
			}

			if (result.mode === "redirect" && result.redirect_url) {
				window.location.href = result.redirect_url;
				return;
			}

			if (result.mode === "poll") {
				setPollTransactionUuid(result.transaction_uuid);
				setView("waiting-momo");
			}
		},
		onError: handleMutationError,
	});

	// Poll transaction status while waiting for a momo approval.
	React.useEffect(() => {
		if (!pollTransactionUuid || view !== "waiting-momo") return;

		let attempts = 0;
		const maxAttempts = 15;

		const interval = setInterval(async () => {
			attempts += 1;

			try {
				const response = await getRegistrationStatus(pollTransactionUuid);
				const status = response.data.status;

				if (status === "completed") {
					clearInterval(interval);
					setResultStatus({ success: true, message: "Payment confirmed. Your registration is complete." });
					setView("result");
				} else if (status === "failed" || status === "cancelled") {
					clearInterval(interval);
					setResultStatus({ success: false, message: "Payment was not completed. Please try again." });
					setView("result");
				} else if (attempts >= maxAttempts) {
					clearInterval(interval);
					setResultStatus({
						success: false,
						message: "We haven't received confirmation yet. If you approved the payment, it may take a few more minutes to reflect.",
					});
					setView("result");
				}
			} catch {
				// keep polling on transient errors
			}
		}, 4000);

		return () => clearInterval(interval);
	}, [pollTransactionUuid, view]);

	const handleSubmit = (values: EventRegistrationFormData) => {
		if (values.senior_waiver_applied && !selectedFeeCategory?.waiver_field_label) {
			toaster("error", "This category does not support a waiver.");
			return;
		}
		submitMutation.mutate(values);
	};

	if (view === "waiting-momo") {
		return (
			<div className="min-h-screen bg-background py-15 flex flex-col items-center justify-center px-4 gap-6">
				<OrgLogo />
				<Card className="max-w-md w-full text-center">
					<CardContent className="pt-8 pb-8 space-y-4">
						<Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
						<h2 className="text-lg font-semibold">Check your phone</h2>
						<p className="text-sm text-muted-foreground">
							Approve the mobile money payment prompt sent to your phone. This page will update automatically once payment is confirmed.
						</p>
					</CardContent>
				</Card>
			</div>
		);
	}

	if (view === "result" && resultStatus) {
		return (
			<div className="min-h-screen bg-background py-15 flex flex-col items-center justify-center px-4 gap-6">
				<OrgLogo />
				<Card className="max-w-md w-full text-center shadow-none">
					<CardContent className="pt-8 pb-8 space-y-4">
						{resultStatus.success ? (
							<CheckCircle2 className="mx-auto h-12 w-12 text-green-600" />
						) : (
							<XCircle className="mx-auto h-12 w-12 text-red-600" />
						)}
						<h2 className="text-lg font-semibold">{resultStatus.success ? "Registration Complete" : "Payment Not Completed"}</h2>
						<p className="text-sm text-muted-foreground">{resultStatus.message}</p>
						{!resultStatus.success && (
							<Button
								onClick={() => {
									setView("form");
									setResultStatus(null);
									setPollTransactionUuid(null);
								}}
							>
								Try Again
							</Button>
						)}
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-background py-15">
			<div className="mx-auto max-w-3xl space-y-6 px-4">
				<div className="text-center space-y-3">
					<OrgLogo className="mx-auto h-16" />
					<h1 className="text-2xl font-bold">GCPS Programme & Event Registration and Payment</h1>
					<p className="text-sm text-muted-foreground">Register and pay for a programme or event below.</p>
				</div>

				<Card className='shadow-none'>
					<CardHeader>
						<CardTitle>Registration</CardTitle>
						<CardDescription>Fields marked required must be completed.</CardDescription>
					</CardHeader>
					<CardContent>
						<Form {...form}>
							<form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6 text-left">
								<FormField
									control={form.control}
									name="event_id"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Select Programme or Event</FormLabel>
											<SelectField
												onValueChange={field.onChange}
												value={field.value}
												placeholder={eventsQuery.isLoading ? "Loading..." : "Select a programme"}
												options={events.map((e: { id: number; name: string }) => ({ id: String(e.id), name: e.name }))}
											/>
											<FormMessage />
										</FormItem>
									)}
								/>

								{!!eventId && (
									<FormField
										control={form.control}
										name="sub_event_id"
										render={({ field }) => (
											<FormItem>
												<FormLabel>Registration Type</FormLabel>
												<SelectField
													onValueChange={field.onChange}
													value={field.value}
													placeholder={subEventsQuery.isLoading ? "Loading..." : "Select registration type"}
													options={subEvents.map((s: { id: number; name: string }) => ({ id: String(s.id), name: s.name }))}
												/>
												<FormMessage />
											</FormItem>
										)}
									/>
								)}

								{!!subEventId && selectedSubEvent?.has_workshops && (
									<FormField
										control={form.control}
										name="workshop_id"
										render={({ field }) => (
											<FormItem>
												<FormLabel>Select Workshop</FormLabel>
												<SelectField
													onValueChange={field.onChange}
													value={field.value}
													placeholder={workshopsQuery.isLoading ? "Loading..." : "Select a workshop"}
													options={workshops.map((w: { id: number; name: string }) => ({ id: String(w.id), name: w.name }))}
												/>
												<FormMessage />
											</FormItem>
										)}
									/>
								)}

								{!!subEventId && (!selectedSubEvent?.has_workshops || !!workshopId) && (
									<>
										<div className="grid gap-4 sm:grid-cols-2">
											<FormField
												control={form.control}
												name="title"
												render={({ field }) => (
													<FormItem>
														<SelectField
															onValueChange={field.onChange}
															value={field.value}
															placeholder="Title"
															label="Title"
															options={TITLE_OPTIONS}
														/>
														<FormMessage />
													</FormItem>
												)}
											/>
											<FormField
												control={form.control}
												name="faculty_id"
												render={({ field }) => (
													<FormItem>
														<SelectField
															onValueChange={field.onChange}
															value={field.value}
															placeholder={facultiesQuery.isLoading ? "Loading..." : "Faculty"}
															label="Faculty"
															options={faculties.map((f: { id: number; name: string }) => ({ id: String(f.id), name: f.name }))}
														/>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
										<div className="grid gap-4 sm:grid-cols-2">
											<FormField
												control={form.control}
												name="first_name"
												render={({ field }) => (
													<FormItem>
														<FormControl>
															<Input label="First Name" {...field} />
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>
											<FormField
												control={form.control}
												name="last_name"
												render={({ field }) => (
													<FormItem>
														<FormControl>
															<Input label="Last Name" {...field} />
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
										<div className="grid gap-4 sm:grid-cols-2">
											<FormField
												control={form.control}
												name="phone"
												render={({ field }) => (
													<FormItem>
														<FormControl>
															<Input label="Telephone Number" {...field} />
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>
											<FormField
												control={form.control}
												name="email"
												render={({ field }) => (
													<FormItem>
														<FormControl>
															<Input type="email" label="Email" {...field} />
														</FormControl>
														<FormDescription>
															A confirmation email will be sent here, so please use an active email address.
														</FormDescription>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
										<FormField
											control={form.control}
											name="confirm_email"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<Input
															type="email"
															label="Confirm Email"
															autoComplete="off"
															onPaste={(e) => e.preventDefault()}
															onDrop={(e) => e.preventDefault()}
															onCopy={(e) => e.preventDefault()}
															{...field}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
										<FormField
											control={form.control}
											name="mdc_number"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<Input label="MDC Number (enter 0000 if you don't have)" {...field} />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="fee_category_id"
											render={({ field }) => (
												<FormItem>
													<FormLabel>Category/Status</FormLabel>
													<SelectField
														onValueChange={field.onChange}
														value={field.value}
														placeholder="Select category"
														options={feeCategories.map((c) => ({ id: String(c.id), name: c.name }))}
													/>
													<FormMessage />
												</FormItem>
											)}
										/>

										{selectedFeeCategory?.allows_other_specify && (
											<FormField
												control={form.control}
												name="category_other"
												render={({ field }) => (
													<FormItem>
														<FormControl>
															<Input label="Please specify" {...field} />
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>
										)}

										{selectedFeeCategory?.waiver_field_label && (
											<FormField
												control={form.control}
												name="senior_waiver_applied"
												render={({ field }) => (
													<FormItem>
														<Label className="mb-2 block">{selectedFeeCategory.waiver_field_label}</Label>
														<RadioGroup
															value={field.value ? "yes" : "no"}
															onValueChange={(v) => field.onChange(v === "yes")}
															className="flex gap-6"
														>
															<div className="flex items-center gap-2">
																<RadioGroupItem value="yes" id="waiver-yes" />
																<Label htmlFor="waiver-yes">Yes (no payment)</Label>
															</div>
															<div className="flex items-center gap-2">
																<RadioGroupItem value="no" id="waiver-no" />
																<Label htmlFor="waiver-no">No</Label>
															</div>
														</RadioGroup>
													</FormItem>
												)}
											/>
										)}

										{selectedFeeCategory && (
											<div className="rounded-lg border bg-muted/30 p-4">
												<div className="text-sm text-muted-foreground">
													{selectedSubEvent?.current_registration_phase === "late_registration"
														? "Late registration fee"
														: "Early bird fee"}
												</div>
												<div className="text-2xl font-bold">{formatGHS(resolvedFee ?? 0)}</div>
											</div>
										)}

										{selectedFeeCategory && resolvedFee !== null && resolvedFee > 0 && (
											<>
												<FormField
													control={form.control}
													name="payment_method"
													render={({ field }) => (
														<FormItem>
															<FormLabel>Payment Method</FormLabel>
															<RadioGroup value={field.value} onValueChange={field.onChange} className="flex gap-6">
																<div className="flex items-center gap-2">
																	<RadioGroupItem value="momo" id="pay-momo" />
																	<Label htmlFor="pay-momo">Mobile Money</Label>
																</div>
																<div className="flex items-center gap-2">
																	<RadioGroupItem value="card" id="pay-card" />
																	<Label htmlFor="pay-card">Card</Label>
																</div>
															</RadioGroup>
														</FormItem>
													)}
												/>

												{paymentMethod === "momo" && (
													<div className="grid gap-4 sm:grid-cols-2">
														<FormField
															control={form.control}
															name="momo_mobile_number"
															render={({ field }) => (
																<FormItem>
																	<FormControl>
																		<Input label="Mobile Money Number" {...field} />
																	</FormControl>
																	<FormMessage />
																</FormItem>
															)}
														/>
														<FormField
															control={form.control}
															name="momo_mobile_network"
															render={({ field }) => (
																<FormItem>
																	<SelectField
																		onValueChange={field.onChange}
																		value={field.value}
																		placeholder="Network"
																		label="Network"
																		options={MOMO_NETWORK_OPTIONS}
																	/>
																	<FormMessage />
																</FormItem>
															)}
														/>
													</div>
												)}
											</>
										)}

										<Button type="submit" className="w-full" disabled={submitMutation.isPending}>
											{submitMutation.isPending
												? "Processing..."
												: resolvedFee === 0
													? "Submit"
													: "Pay and Submit"}
										</Button>
									</>
								)}
							</form>
						</Form>
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
